import {getTestProps} from '@benchling/js-test-utils';
import React from 'react';
import _ from 'underscore';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import HoverIcon from 'nav/expanded/HoverIcon';
import Popover from 'ui-library/floating-ui/Popover';
import AddIcon from 'ui-library/icons/AddIcon';
import ModalAnchor from 'ui-library/ModalAnchor';
import ReorderableSimpleTable from 'ui-library/reorderable/ReorderableSimpleTable';
import {Cell, Row} from 'ui-library/reorderable/ReorderableSimpleTableConstants';
import * as modals from 'util/Modals';
import StageEntryTemplateLink from 'workflows/entry-templates/StageEntryTemplateLink';
import WorkflowEntryTemplateModal from 'workflows/entry-templates/WorkflowEntryTemplateModal';
import {
  ExperimentalConditionPopoverContent,
  experimentalConditionValuesToString,
} from 'workflows/util/ExperimentalConditionUtil';
import * as WorkflowTemplateActions from 'workflows/WorkflowTemplateActions';

import {StageConfiguration} from '../WorkflowConstants';

export interface StageConfigurationTableProps {
  isEditable: boolean;
  onCreateStageConfiguration: () => void;
  onEditStageConfiguration: (stageConfiguration: StageConfiguration) => void;
  stageConfigurations: Array<StageConfiguration>;
}

interface State {
  // Id of the stage configuration being edited, or null when the WorkflowEntryTemplateModal is closed. The
  // full configuration is derived from props at render time so it always reflects the latest tags/tagSchema
  // and can never be resurrected out of state after the modal is closed.
  editingStageEntryTemplateId: string | null;
}

export default class StageConfigurationTable extends React.Component<StageConfigurationTableProps, State> {
  override state: State = {
    editingStageEntryTemplateId: null,
  };

  _handleStageEntryTemplateClicked = (stageEntryTemplate: StageConfiguration): void => {
    this.setState({editingStageEntryTemplateId: stageEntryTemplate.id});
  };

  _handleCloseStageEntryTemplateModal = (): void => {
    this.setState({editingStageEntryTemplateId: null});
  };

  // The configuration last rendered into the open modal. Used as a fallback when the configuration is
  // momentarily absent from props (e.g. a store refresh replaces the stage list for a render). Unmounting
  // the modal discards in-progress edits, so a transient gap must not be allowed to close it.
  _lastEditingStageEntryTemplate: StageConfiguration | null = null;

  _getEditingStageEntryTemplate(): StageConfiguration | null {
    const {editingStageEntryTemplateId} = this.state;
    if (editingStageEntryTemplateId == null) {
      // The modal closes only on an explicit open/close (id cleared), never because the configuration
      // dropped out of props. Reset the fallback so the next edit starts from live data.
      this._lastEditingStageEntryTemplate = null;
      return null;
    }
    const fromProps = this.props.stageConfigurations.find((sc) => sc.id === editingStageEntryTemplateId);
    if (fromProps != null) {
      this._lastEditingStageEntryTemplate = fromProps;
    }
    return this._lastEditingStageEntryTemplate;
  }

  _handleDeleteStageConfiguration = (stageConfiguration: StageConfiguration): void => {
    modals
      .confirm(
        'Confirm Delete Stage',
        `Are you sure you want to delete the stage ${stageConfiguration.stageName}?` +
          ` This action cannot be undone.`,
        'danger'
      )
      .done(() => {
        WorkflowTemplateActions.deleteStageConfiguration(stageConfiguration.id);
      });
  };

  _handleReorderStageConfiguration = (stageConfigurationId: string, newIndex: number): void => {
    WorkflowTemplateActions.reorderStageConfiguration(stageConfigurationId, newIndex);
  };

  _getHeaderCells(): Array<Cell> {
    return [
      {
        key: 'stage',
        contents: (
          <div className="u-vertical-center-items">
            <span>Stage</span>
            {this.props.isEditable && <AddIcon onClick={this.props.onCreateStageConfiguration} />}
          </div>
        ),
      },
      {
        key: 'template',
        contents: 'Entry Template',
      },
      {
        key: 'conditions',
        contents: 'Experimental Conditions',
      },
      {
        key: 'hover icons',
        contents: '',
      },
    ];
  }

  _getRows(): Array<Row> {
    return this.props.stageConfigurations.map((stageConfiguration) => {
      // eslint-disable-next-line no-restricted-syntax
      const experimentalConditionValues = _.flatten(
        stageConfiguration.experimentalConditions.map((ec) => ec.values)
      );
      const hoverIcons = this.props.isEditable && (
        <div className="stageConfigurationTable-hoverIcons">
          <HoverIcon
            aria-label="Edit stage configuration"
            key={`edit-${stageConfiguration.id}`}
            icon="edit"
            onClick={this.props.onEditStageConfiguration}
            result={stageConfiguration}
          />
          <HoverIcon
            aria-label="Delete stage configuration"
            key={`delete-${stageConfiguration.id}`}
            icon="delete"
            onClick={this._handleDeleteStageConfiguration}
            result={stageConfiguration}
          />
        </div>
      );

      return {
        id: stageConfiguration.id,
        cells: [
          {
            key: 'name',
            contents: stageConfiguration.stageName,
          },
          {
            key: 'template',
            contents: (
              <StageEntryTemplateLink
                stageConfiguration={stageConfiguration}
                onClick={() => {
                  this._handleStageEntryTemplateClicked(stageConfiguration);
                }}
              />
            ),
          },
          {
            key: 'conditions',
            contents: experimentalConditionValues.length ? (
              <Popover
                renderContent={() => (
                  <ExperimentalConditionPopoverContent
                    experimentalConditions={stageConfiguration.experimentalConditions}
                  />
                )}
              >
                <span>{experimentalConditionValuesToString(experimentalConditionValues)}</span>
              </Popover>
            ) : (
              <span className="u-faded">None</span>
            ),
          },
          {
            key: 'hover icons',
            contentsClassName: 'u-justify-content-end',
            contents: hoverIcons,
          },
        ],
      };
    });
  }

  override render(): JSX.Element {
    const editingStageEntryTemplate = this._getEditingStageEntryTemplate();
    return (
      <div {...getTestProps({component: 'StageConfigurationTable', element: 'root'})}>
        {editingStageEntryTemplate && (
          <ModalAnchor open={true}>
            <WorkflowEntryTemplateModal
              entryTemplate={editingStageEntryTemplate}
              fileType={ModelTypeConstants.STAGE_CONFIGURATION}
              isReadonly={!this.props.isEditable}
              onClose={this._handleCloseStageEntryTemplateModal}
            />
          </ModalAnchor>
        )}
        <ReorderableSimpleTable
          dataTestAttrs={{component: 'StageConfigurationTable', element: 'table'}}
          headerCells={this._getHeaderCells()}
          isDraggingDisabled={!this.props.isEditable}
          noRowsDisplay="No stages"
          onReorderRow={this._handleReorderStageConfiguration}
          rows={this._getRows()}
        />
      </div>
    );
  }
}
